import React, { useEffect, useState, useMemo } from "react";
import authApi from "../../../../api/authApi";
import formattedMoney from "../../../../utils/formatMoney";
import formatDate from "../../../../utils/formatDate";
import hideStringEmail from "../../../../utils/hideStringEmail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faSearch } from "@fortawesome/free-solid-svg-icons";

// Badge trạng thái hạn thưởng giới thiệu của từng người được mời
function RefStatusBadge({ isExpired, daysLeft }) {
    if (isExpired) {
        return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-surface-raised text-content-2">Hết hạn</span>;
    }
    if (daysLeft <= 15) {
        return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Sắp hết · {daysLeft} ngày</span>;
    }
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Còn hạn · {daysLeft} ngày</span>;
}

function RefUsers() {
    const [refUsers, setRefUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [totalRefCommission, setTotalCommisson] = useState(0);
    const [totalApprovedCommission, setTotalApprovedCommission] = useState(0);
    const [totalPendingCommission, setTotalPendingCommission] = useState(0);
    const [totalOrders, setTotalOrders] = useState(0);
    const [totalRefUsers, setTotalRefUsers] = useState(0);
    const [activeRefUsers, setActiveRefUsers] = useState(0);
    const [expiredRefUsers, setExpiredRefUsers] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | active | expired

    useEffect(() => {
        const fetchRefs = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const { data } = await authApi.myRefs();
                setRefUsers(data.data.refUsers || []);
                setTotalCommisson(data.data.totalRefCommission || 0);
                setTotalApprovedCommission(data.data.totalApprovedCommission || 0);
                setTotalPendingCommission(data.data.totalPendingCommission || 0);
                setTotalOrders(data.data.totalOrders || 0);
                setTotalRefUsers(data.data.totalRefUsers || 0);
                setActiveRefUsers(data.data.activeRefUsers || 0);
                setExpiredRefUsers(data.data.expiredRefUsers || 0);
            } catch (error) {
                console.error("Error fetching referral data:", error);
                setError("Không thể tải dữ liệu. Vui lòng thử lại sau.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchRefs();
    }, []);

    // Filter users theo trạng thái hạn thưởng + từ khóa tìm kiếm
    useEffect(() => {
        const term = searchTerm.toLowerCase().trim();
        const filtered = refUsers.filter((user) => {
            // Lọc trạng thái
            if (statusFilter === "active" && user.isExpired) return false;
            if (statusFilter === "expired" && !user.isExpired) return false;
            // Lọc từ khóa
            if (!term) return true;
            const userId = String(user.userId || "").toLowerCase();
            const name = (user.name || "").toLowerCase();
            const email = (user.email || "").toLowerCase();
            return userId.includes(term) || name.includes(term) || email.includes(term);
        });
        setFilteredUsers(filtered);
    }, [searchTerm, statusFilter, refUsers]);

    const formattedCommission = useMemo(() => {
        return formattedMoney.format(totalRefCommission);
    }, [totalRefCommission]);

    const formattedApprovedCommission = useMemo(() => {
        return formattedMoney.format(totalApprovedCommission);
    }, [totalApprovedCommission]);

    const formattedPendingCommission = useMemo(() => {
        return formattedMoney.format(totalPendingCommission);
    }, [totalPendingCommission]);

    if (isLoading) {
        return (
            <section className="bg-surface rounded-lg shadow p-4">
                <div className="flex items-center justify-center gap-2 py-8">
                    <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-xl text-primary-500" />
                    <span className="text-content-2">Đang tải thống kê...</span>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="bg-surface rounded-lg shadow p-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="font-semibold text-red-700 mb-1">Lỗi</p>
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-surface rounded-lg shadow p-4 text-sm">
            <p className="font-bold text-lg mb-4">Thống kê của bạn</p>

            {/* Tổng quan */}
            <div className="flex flex-col gap-3 mb-4">
                <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-content-2">Đã giới thiệu:</span>
                    <span className="font-semibold text-primary-600">{totalRefUsers} người dùng</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-content-2">Đang hưởng hoa hồng / Hết hạn:</span>
                    <span className="font-semibold">
                        <span className="text-green-600">{activeRefUsers}</span>
                        <span className="text-content-3"> / </span>
                        <span className="text-content-3">{expiredRefUsers}</span>
                    </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-content-2">Tổng số đơn hàng:</span>
                    <span className="font-semibold text-primary-600">{totalOrders} đơn</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-content-2">Hoa hồng đã duyệt:</span>
                    <span className="font-bold text-green-600">{formattedApprovedCommission}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-content-2">Hoa hồng chờ duyệt:</span>
                    <span className="font-bold text-orange-600">{formattedPendingCommission}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                    <span className="text-content-2 font-semibold">Tổng hoa hồng:</span>
                    <span className="font-bold text-primary-600 text-lg">{formattedCommission}</span>
                </div>
            </div>

            {refUsers.length === 0 ? (
                <div className="mt-4 pt-4 border-t">
                    <p className="text-content-2 text-center py-4">Bạn chưa giới thiệu được ai. Hãy chia sẻ link giới thiệu để nhận hoa hồng nhé!</p>
                </div>
            ) : (
                <div className="mt-4 pt-4 border-t">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-3">
                        <p className="font-semibold">Danh sách người được giới thiệu:</p>
                        {/* Search Box */}
                        <div className="relative w-full md:w-64">
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo ID, tên, email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-content-3" />
                        </div>
                    </div>

                    {/* Filter Tabs theo trạng thái */}
                    <div className="flex gap-2 mb-3">
                        {[
                            { key: "all", label: `Tất cả (${totalRefUsers})` },
                            { key: "active", label: `Còn hạn (${activeRefUsers})` },
                            { key: "expired", label: `Hết hạn (${expiredRefUsers})` },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setStatusFilter(tab.key)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                    statusFilter === tab.key ? "bg-primary-500 text-white" : "bg-surface-muted text-content-2 hover:bg-surface-raised"
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {filteredUsers.length === 0 ? (
                        <div className="text-center py-8 text-content-3">
                            <p>Không tìm thấy kết quả phù hợp</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-sm min-w-[800px]">
                                    <thead>
                                        <tr className="border-b bg-surface-sunken">
                                            <th className="text-left py-2 px-2 font-semibold whitespace-nowrap">ID</th>
                                            <th className="text-left py-2 px-2 font-semibold whitespace-nowrap">Tên</th>
                                            <th className="text-left py-2 px-2 font-semibold whitespace-nowrap">Ngày tham gia</th>
                                            <th className="text-center py-2 px-2 font-semibold whitespace-nowrap">Trạng thái</th>
                                            <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Tổng đơn</th>
                                            <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Đơn hoàn thành</th>
                                            <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Đã duyệt</th>
                                            <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Chờ duyệt</th>
                                            <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Tổng hoa hồng</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map((user, index) => (
                                            <tr key={user.userId || index} className={`border-b hover:bg-surface-sunken ${user.isExpired ? "opacity-60" : ""}`}>
                                                <td className="py-2 px-2 whitespace-nowrap">{user.userId}</td>
                                                <td className="py-2 px-2 whitespace-nowrap">{user.name || "-"}</td>
                                                <td className="py-2 px-2 text-content-2 whitespace-nowrap">{formatDate(user.createTime)}</td>
                                                <td className="py-2 px-2 text-center whitespace-nowrap"><RefStatusBadge isExpired={user.isExpired} daysLeft={user.daysLeft} /></td>
                                                <td className="py-2 px-2 text-right whitespace-nowrap">{user.totalOrders || 0}</td>
                                                <td className="py-2 px-2 text-right whitespace-nowrap">{user.completedOrders || 0}</td>
                                                <td className="py-2 px-2 text-right font-semibold text-green-600 whitespace-nowrap">{formattedMoney.format(user.approvedCommission || 0)}</td>
                                                <td className="py-2 px-2 text-right font-semibold text-orange-600 whitespace-nowrap">{formattedMoney.format(user.pendingCommission || 0)}</td>
                                                <td className="py-2 px-2 text-right font-semibold text-primary-600 whitespace-nowrap">{formattedMoney.format(user.totalCommission || 0)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Mobile Card List */}
                            <div className="md:hidden space-y-3">
                                {filteredUsers.map((user, index) => (
                                    <div key={user.userId || index} className={`bg-surface-sunken rounded-lg p-3 border border-line ${user.isExpired ? "opacity-60" : ""}`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <p className="font-semibold">{user.name || `User ${user.userId}`}</p>
                                                <p className="text-xs text-content-3">ID: {user.userId}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-primary-600">{formattedMoney.format(user.totalCommission || 0)}</p>
                                                <p className="text-xs text-content-3">{user.totalOrders || 0} đơn</p>
                                            </div>
                                        </div>
                                        <div className="text-xs text-content-2 space-y-1 pt-2 border-t">
                                            <div className="flex justify-between items-center">
                                                <span>Tham gia: {formatDate(user.createTime)}</span>
                                                <RefStatusBadge isExpired={user.isExpired} daysLeft={user.daysLeft} />
                                            </div>
                                            <div className="flex justify-between pt-1">
                                                <span>Đơn hoàn thành:</span>
                                                <span className="font-semibold">{user.completedOrders || 0}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-green-600">Đã duyệt:</span>
                                                <span className="font-semibold text-green-600">{formattedMoney.format(user.approvedCommission || 0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-orange-600">Chờ duyệt:</span>
                                                <span className="font-semibold text-orange-600">{formattedMoney.format(user.pendingCommission || 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}

export default RefUsers;
